<template>
  <BaseLayout>
    <div class="max-w-3xl mx-auto">
      <!-- Header -->
      <div class="mb-8">
        <div class="flex items-center">
          <router-link
            to="/users"
            class="text-gray-500 hover:text-gray-700 mr-4"
          >
            <ArrowLeftIcon class="h-5 w-5" />
          </router-link>
          <div>
            <h1 class="text-2xl font-semibold text-gray-900">Editar Usuario</h1>
            <p class="mt-1 text-sm text-gray-600">
              Modifique la información del usuario seleccionado.
            </p>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loadingUser" class="bg-white shadow rounded-lg p-8 text-center">
        <div class="loading-spinner mx-auto"></div>
        <p class="mt-2 text-sm text-gray-500">Cargando información del usuario...</p>
      </div>

      <!-- Form -->
      <form v-else-if="user" @submit.prevent="handleSubmit" class="space-y-8">
        <!-- Información Personal -->
        <div class="bg-white shadow rounded-lg">
          <div class="px-4 py-5 sm:p-6">
            <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
              Información Personal
            </h3>
            
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label class="form-label">Nombre *</label>
                <input
                  v-model="form.firstName"
                  type="text"
                  required
                  class="form-input"
                  :class="{ 'border-red-300': errors.firstName }"
                  placeholder="Ingrese el nombre"
                />
                <p v-if="errors.firstName" class="form-error">{{ errors.firstName }}</p>
              </div>

              <div>
                <label class="form-label">Apellido *</label>
                <input
                  v-model="form.lastName"
                  type="text"
                  required
                  class="form-input"
                  :class="{ 'border-red-300': errors.lastName }"
                  placeholder="Ingrese el apellido"
                />
                <p v-if="errors.lastName" class="form-error">{{ errors.lastName }}</p>
              </div>

              <div>
                <label class="form-label">DNI</label>
                <input
                  v-model="form.dni"
                  type="text"
                  class="form-input"
                  placeholder="Número de documento"
                />
              </div>

              <div>
                <label class="form-label">Teléfono</label>
                <input
                  v-model="form.telefono"
                  type="text"
                  class="form-input"
                  placeholder="Número de teléfono"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Información de Cuenta -->
        <div class="bg-white shadow rounded-lg">
          <div class="px-4 py-5 sm:p-6">
            <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
              Información de Cuenta
            </h3>
            
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label class="form-label">Usuario *</label>
                <input
                  v-model="form.username"
                  type="text"
                  required
                  class="form-input"
                  :class="{ 'border-red-300': errors.username }"
                  placeholder="Nombre de usuario único"
                />
                <p v-if="errors.username" class="form-error">{{ errors.username }}</p>
                <p class="text-xs text-gray-500 mt-1">
                  Debe ser único en el sistema. Mínimo 3 caracteres.
                </p>
              </div>

              <div>
                <label class="form-label">Email</label>
                <input
                  v-model="form.email"
                  type="email"
                  class="form-input"
                  :class="{ 'border-red-300': errors.email }"
                  placeholder="correo@ejemplo.com (opcional)"
                />
                <p v-if="errors.email" class="form-error">{{ errors.email }}</p>
                <p class="text-xs text-gray-500 mt-1">
                  Campo opcional. Puede dejarse vacío.
                </p>
              </div>

              <div>
                <label class="form-label">Estado</label>
                <select
                  v-model="form.enabled"
                  class="form-input"
                >
                  <option :value="true">Activo</option>
                  <option :value="false">Inactivo</option>
                </select>
                <p class="text-xs text-gray-500 mt-1">
                  Los usuarios inactivos no pueden acceder al sistema.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Rol y Permisos -->
        <div class="bg-white shadow rounded-lg">
          <div class="px-4 py-5 sm:p-6">
            <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
              Rol y Permisos
            </h3>
            
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label class="form-label">Rol del Usuario *</label>
                <select
                  v-model="form.role"
                  required
                  class="form-input"
                  :class="{ 'border-red-300': errors.role }"
                  @change="handleRoleChange"
                >
                  <option value="">Seleccione un rol</option>
                  <option value="admin">Administrador</option>
                  <option value="validador">Validador</option>
                  <option value="digitador">Digitador</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="transmisor">Transmisor</option>
                  <option value="auditor">Auditor</option>
                </select>
                <p v-if="errors.role" class="form-error">{{ errors.role }}</p>
                <div class="mt-2 text-sm text-gray-600">
                  <div v-if="form.role" class="p-3 bg-blue-50 rounded-md">
                    <p class="font-medium text-blue-900">{{ getRoleDescription(form.role) }}</p>
                    <p class="text-blue-700 mt-1">{{ getRolePermissions(form.role) }}</p>
                  </div>
                </div>
              </div>

              <div v-if="form.role === 'transmisor' || form.role === 'auditor'" class="col-span-2">
                <label class="form-label">Establecimientos *</label>
                <input
                  v-model="establecimientosInput"
                  type="text"
                  :required="form.role === 'transmisor' || form.role === 'auditor'"
                  class="form-input"
                  :class="{ 'border-red-300': errors.establecimientosIds }"
                  placeholder="Ingrese IDs separados por comas o pipes (ej: 54, 55, 56 o 54|55|56)"
                  @input="handleEstablecimientosInput"
                  @keydown.enter.prevent="addEstablecimiento"
                />
                <p v-if="errors.establecimientosIds" class="form-error">{{ errors.establecimientosIds }}</p>
                <p class="text-xs text-gray-500 mt-1">
                  Puede ingresar múltiples IDs separados por comas (,) o pipes (|). Presione Enter para agregar.
                </p>
                
                <!-- Chips/Tags de establecimientos -->
                <div v-if="form.establecimientosIds.length > 0" class="flex flex-wrap gap-2 mt-3">
                  <span
                    v-for="(id, index) in form.establecimientosIds"
                    :key="index"
                    class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                  >
                    {{ id }}
                    <button
                      type="button"
                      @click="removeEstablecimiento(index)"
                      class="ml-2 inline-flex items-center justify-center w-4 h-4 text-blue-600 hover:text-blue-800 hover:bg-blue-200 rounded-full"
                    >
                      ×
                    </button>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Cambio de Contraseña -->
        <div class="bg-white shadow rounded-lg">
          <div class="px-4 py-5 sm:p-6">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg leading-6 font-medium text-gray-900">
                Cambiar Contraseña
              </h3>
              <button
                type="button"
                @click="showPasswordSection = !showPasswordSection"
                class="text-sm text-primary-600 hover:text-primary-500"
              >
                {{ showPasswordSection ? 'Cancelar' : 'Cambiar contraseña' }}
              </button>
            </div>
            
            <div v-if="showPasswordSection" class="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label class="form-label">Nueva Contraseña *</label>
                <input
                  v-model="passwordForm.password"
                  type="password"
                  class="form-input"
                  :class="{ 'border-red-300': errors.password }"
                  placeholder="Mínimo 6 caracteres"
                />
                <p v-if="errors.password" class="form-error">{{ errors.password }}</p>
              </div>

              <div>
                <label class="form-label">Confirmar Contraseña *</label>
                <input
                  v-model="passwordForm.confirmPassword"
                  type="password"
                  class="form-input"
                  :class="{ 'border-red-300': errors.confirmPassword }"
                  placeholder="Repita la contraseña"
                />
                <p v-if="errors.confirmPassword" class="form-error">{{ errors.confirmPassword }}</p>
              </div>

              <div class="sm:col-span-2">
                <label class="flex items-center">
                  <input
                    v-model="passwordForm.temporary"
                    type="checkbox"
                    class="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                  />
                  <span class="ml-2 text-sm text-gray-600">
                    Contraseña temporal (el usuario deberá cambiarla en el próximo login)
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex justify-end space-x-3">
          <router-link
            to="/users"
            class="btn-secondary"
          >
            Cancelar
          </router-link>
          <button
            type="submit"
            :disabled="loading"
            class="btn-primary"
          >
            <span v-if="!loading">Guardar Cambios</span>
            <span v-else class="flex items-center">
              <div class="loading-spinner mr-2"></div>
              Guardando...
            </span>
          </button>
        </div>
      </form>

      <!-- Error State -->
      <div v-else class="bg-white shadow rounded-lg p-8 text-center">
        <ExclamationTriangleIcon class="mx-auto h-12 w-12 text-red-400" />
        <h3 class="mt-2 text-sm font-medium text-gray-900">Error cargando usuario</h3>
        <p class="mt-1 text-sm text-gray-500">
          No se pudo cargar la información del usuario.
        </p>
        <div class="mt-6">
          <router-link to="/users" class="btn-primary">
            Volver a usuarios
          </router-link>
        </div>
      </div>
    </div>
  </BaseLayout>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { apiService } from '@/services/apiService'
import { useToast } from 'vue-toastification'
import BaseLayout from '@/components/BaseLayout.vue'
import { ArrowLeftIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline'

const router = useRouter()
const route = useRoute()
const toast = useToast()

const loading = ref(false)
const loadingUser = ref(true)
const showPasswordSection = ref(false)
const user = ref(null)

const form = reactive({
  firstName: '',
  lastName: '',
  dni: '',
  telefono: '',
  username: '',
  email: '',
  role: '',
  enabled: true,
  establecimientoId: null as number | null, // Legacy - mantener por compatibilidad
  establecimientosIds: [] as number[]
})

const establecimientosInput = ref('')

const passwordForm = reactive({
  password: '',
  confirmPassword: '',
  temporary: false
})

const errors = reactive({
  firstName: '',
  lastName: '',
  username: '',
  email: '',
  role: '',
  establecimientoId: '',
  establecimientosIds: '',
  password: '',
  confirmPassword: ''
})

function clearErrors() {
  Object.keys(errors).forEach(key => {
    errors[key as keyof typeof errors] = ''
  })
}

function validateForm(): boolean {
  clearErrors()
  let isValid = true

  // Validar campos requeridos
  if (!form.firstName.trim()) {
    errors.firstName = 'El nombre es requerido'
    isValid = false
  }

  if (!form.lastName.trim()) {
    errors.lastName = 'El apellido es requerido'
    isValid = false
  }

  if (!form.username.trim()) {
    errors.username = 'El usuario es requerido'
    isValid = false
  } else if (form.username.length < 3) {
    errors.username = 'El usuario debe tener al menos 3 caracteres'
    isValid = false
  }

  // Email es opcional, pero si se proporciona debe ser válido
  if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.email = 'El email no es válido'
    isValid = false
  }

  if (!form.role) {
    errors.role = 'Seleccione un rol'
    isValid = false
  }

  if ((form.role === 'transmisor' || form.role === 'auditor') && form.establecimientosIds.length === 0) {
    errors.establecimientosIds = 'Debe ingresar al menos un establecimiento para transmisores y auditores'
    isValid = false
  }

  // Validar contraseña si se está cambiando
  if (showPasswordSection.value) {
    if (!passwordForm.password) {
      errors.password = 'La contraseña es requerida'
      isValid = false
    } else if (passwordForm.password.length < 6) {
      errors.password = 'La contraseña debe tener al menos 6 caracteres'
      isValid = false
    }

    if (!passwordForm.confirmPassword) {
      errors.confirmPassword = 'Confirme la contraseña'
      isValid = false
    } else if (passwordForm.password !== passwordForm.confirmPassword) {
      errors.confirmPassword = 'Las contraseñas no coinciden'
      isValid = false
    }
  }

  return isValid
}

function handleRoleChange() {
  if (form.role !== 'transmisor' && form.role !== 'auditor') {
    form.establecimientoId = null
    form.establecimientosIds = []
    establecimientosInput.value = ''
  }
}

// Parsear IDs desde string (comas o pipes)
function parseEstablecimientosFromString(value: string): number[] {
  if (!value || !value.trim()) return []
  
  const ids = value
    .split(/[,|]/)
    .map(id => parseInt(id.trim()))
    .filter(id => !isNaN(id) && id > 0)
  
  return [...new Set(ids)] // Eliminar duplicados
}

// Manejar input de establecimientos
function handleEstablecimientosInput() {
  const parsed = parseEstablecimientosFromString(establecimientosInput.value)
  if (parsed.length > 0) {
    form.establecimientosIds = parsed
    errors.establecimientosIds = ''
  }
}

// Agregar establecimiento (al presionar Enter)
function addEstablecimiento() {
  handleEstablecimientosInput()
}

// Remover un establecimiento específico
function removeEstablecimiento(index: number) {
  form.establecimientosIds.splice(index, 1)
  
  // Actualizar el input para reflejar los IDs restantes
  if (form.establecimientosIds.length > 0) {
    establecimientosInput.value = form.establecimientosIds.join(', ')
  } else {
    establecimientosInput.value = ''
  }
}

function getRoleDescription(role: string): string {
  const descriptions: Record<string, string> = {
    admin: 'Administrador del Sistema',
    validador: 'Validador de Actas',
    digitador: 'Digitador de Datos',
    supervisor: 'Supervisor de Procesos',
    transmisor: 'Transmisor de Establecimiento',
    auditor: 'Auditor de Establecimiento'
  }
  return descriptions[role] || ''
}

function getRolePermissions(role: string): string {
  const permissions: Record<string, string> = {
    admin: 'Acceso completo al sistema de administración',
    validador: 'Validación de actas electorales',
    digitador: 'Digitación de datos de actas',
    supervisor: 'Supervisión de procesos electorales',
    transmisor: 'Transmisión de actas desde establecimiento',
    auditor: 'Auditoría de actas desde establecimiento'
  }
  return permissions[role] || ''
}

// Mapear roles de backend a frontend
function mapBackendRole(backendRole: string): string {
  const roleMapping: Record<string, string> = {
    'admin': 'admin',
    'VALIDACION': 'validador',
    'DIGITACION': 'digitador',
    'SUPERVISION': 'supervisor',
    'TRANSMISION': 'transmisor',
    'AUDITOR': 'auditor'
  }
  return roleMapping[backendRole] || backendRole
}

// Mapear roles de frontend a backend
function mapFrontendRole(frontendRole: string): string {
  const roleMapping: Record<string, string> = {
    'admin': 'admin',
    'validador': 'VALIDACION',
    'digitador': 'DIGITACION',
    'supervisor': 'SUPERVISION',
    'transmisor': 'TRANSMISION',
    'auditor': 'AUDITOR'
  }
  return roleMapping[frontendRole] || frontendRole
}

async function loadUser() {
  try {
    loadingUser.value = true
    const userId = route.params.id as string
    
    const userData = await apiService.getUserById(userId) as any
    user.value = userData
    
    // Llenar formulario
    form.firstName = userData.firstName || ''
    form.lastName = userData.lastName || ''
    form.dni = userData.dni || ''
    form.telefono = userData.telefono || ''
    form.username = userData.username || ''
    form.email = userData.email || ''
    form.role = mapBackendRole(userData.role) || ''
    form.enabled = userData.enabled !== false
    
    // Cargar establecimientos (priorizar array sobre single)
    if (userData.establecimientosIds && userData.establecimientosIds.length > 0) {
      form.establecimientosIds = userData.establecimientosIds
      establecimientosInput.value = userData.establecimientosIds.join(', ')
    } else if (userData.establecimientoId) {
      // Fallback a single establecimientoId (legacy)
      form.establecimientosIds = [userData.establecimientoId]
      establecimientosInput.value = String(userData.establecimientoId)
    } else {
      form.establecimientosIds = []
      establecimientosInput.value = ''
    }
    form.establecimientoId = userData.establecimientoId || null
    
  } catch (error: any) {
    toast.error('Error cargando usuario: ' + error.message)
    user.value = null
  } finally {
    loadingUser.value = false
  }
}

async function handleSubmit() {
  if (!validateForm()) {
    toast.error('Por favor corrija los errores en el formulario')
    return
  }

  try {
    loading.value = true
    const userId = route.params.id as string

    // Asegurar que los establecimientos estén actualizados desde el input
    handleEstablecimientosInput()
    
    // Preparar datos de actualización
    const updateData: any = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      username: form.username.trim(),
      email: form.email.trim().toLowerCase(),
      role: mapFrontendRole(form.role),
      enabled: form.enabled,
      dni: form.dni.trim() || undefined,
      telefono: form.telefono.trim() || undefined,
      establecimientosIds: form.establecimientosIds.length > 0 ? form.establecimientosIds : undefined
    }

    // Actualizar usuario
    await apiService.updateUser(userId, updateData)

    // Cambiar contraseña si se especificó
    if (showPasswordSection.value && passwordForm.password) {
      await apiService.changeUserPassword(userId, passwordForm.password, passwordForm.temporary)
    }

    toast.success('Usuario actualizado exitosamente')
    router.push('/users')

  } catch (error: any) {
    if (error.response?.status === 409) {
      if (error.response.data.message?.includes('username')) {
        errors.username = 'Este nombre de usuario ya está en uso'
      } else if (error.response.data.message?.includes('email')) {
        errors.email = 'Este email ya está registrado'
      } else {
        toast.error('Conflicto al actualizar usuario')
      }
    } else {
      // Extraer el mensaje de error del backend
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message
      toast.error(errorMessage)
    }
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadUser()
})
</script>
